import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, X, type LucideIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/cn';

export type NavEntry = {
  testId: string;
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /**
   * Other paths this link stands for — a supervisor category is active on
   * every one of its pages, not only the first (#12).
   */
  match?: string[];
};

/**
 * The sections, as a sidebar that collapses — the Saleem apps' layout, dressed
 * in the redesign (DESIGN.md): the logo at the top, each section a 48px
 * rounded row with the current one filled in the main colour, and the person
 * at the bottom.
 *
 * Three states, not two: expanded (labels), collapsed to an icon rail on a
 * wide screen, and an off-canvas drawer on a narrow one. The drawer matters
 * most — the first version of this was `hidden md:block`, which left a phone
 * with no navigation whatsoever.
 *
 * ONE deliberate departure from the Saleem original: a collapsed link keeps an
 * aria-label. There the icon rail relies on a hover tooltip, which a screen
 * reader never sees and a touch screen cannot produce, so those links have no
 * accessible name at all.
 */
export function Sidebar({
  entries, open, setOpen, collapsed, toggleCollapse, footer,
}: {
  entries: NavEntry[];
  open: boolean;
  setOpen: (open: boolean) => void;
  collapsed: boolean;
  toggleCollapse: () => void;
  /** The person, pinned to the bottom of the column. */
  footer?: ReactNode;
}) {
  const { t, dir } = useLanguage();
  const { pathname } = useLocation();
  const isRTL = dir === 'rtl';
  const covers = (paths: string[] | undefined) => (paths ?? []).some((p) => pathname === p || pathname.startsWith(`${p}/`));
  // Teal calligraphy on paper, ivory on night.
  const mark = useTheme().scheme === 'dark' ? '/brand/mark-ivory.png' : '/brand/mark-teal.png';

  return (
    <>
      {open && (
        <div
          data-testid="sidebar-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden"
        />
      )}

      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? 'true' : undefined}
        className={cn(
          'fixed inset-y-0 start-0 z-50 flex w-64 flex-col border-e border-hair bg-card transition-all duration-300 ease-in-out lg:translate-x-0',
          collapsed ? 'lg:w-[76px]' : 'lg:w-64',
          open ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full',
        )}
      >
        {/* The logo, with the drawer's close (narrow) or the rail toggle (wide). */}
        <div
          className={cn(
            'flex h-20 shrink-0 items-center justify-between gap-2 px-5',
            collapsed && 'lg:justify-center lg:px-2',
          )}
        >
          <span className={cn('flex min-w-0 items-center gap-2.5', collapsed && 'lg:hidden')}>
            <img
              src={mark}
              alt={t('brand.name')}
              data-testid="sidebar-mark"
              className="h-10 w-auto"
            />
            {/* The motto beside the mark (owner, 2026-10-10). */}
            <span aria-hidden="true" data-testid="sidebar-motto" className="truncate text-[15px] font-semibold text-accent">
              {t('brand.motto')}
            </span>
          </span>
          <button
            type="button"
            data-testid="sidebar-close"
            onClick={() => setOpen(false)}
            aria-label={t('nav.close')}
            className="flex h-9 w-9 items-center justify-center rounded-full text-helper hover:bg-surface hover:text-ink lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
          {/* Collapsing is a wide-screen idea; the narrow one has a drawer. */}
          <button
            type="button"
            data-testid="sidebar-collapse"
            onClick={toggleCollapse}
            aria-label={t(collapsed ? 'nav.expand' : 'nav.collapse')}
            title={t(collapsed ? 'nav.expand' : 'nav.collapse')}
            className="hidden h-9 w-9 items-center justify-center rounded-full text-helper transition-colors hover:bg-surface hover:text-ink lg:flex"
          >
            {collapsed === isRTL
              ? <PanelLeftClose className="h-[18px] w-[18px]" strokeWidth={1.8} />
              : <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.8} />}
          </button>
        </div>

        <nav
          data-testid="global-nav"
          className={cn('flex-1 space-y-1.5 overflow-y-auto px-4 pb-4 pt-2', collapsed && 'lg:px-3')}
        >
          {entries.map(({ testId, to, labelKey, icon: Icon, match }) => {
            // The same prefix rule NavLink applied, widened to a category's
            // other pages (#12): «الأشخاص» stays lit on /admin/schools too.
            const isActive = covers([to, ...(match ?? [])]);
            return (
              <Link
                key={testId}
                to={to}
                data-testid={testId}
                onClick={() => setOpen(false)}
                aria-current={isActive ? 'page' : undefined}
                // Kept even when the label is hidden: the icon rail must still
                // say what each link is.
                aria-label={t(labelKey)}
                title={collapsed ? t(labelKey) : undefined}
                className={cn(
                  'flex h-12 items-center gap-3 rounded-[14px] px-3.5 text-base transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  collapsed && 'lg:justify-center lg:px-0',
                  isActive
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : 'text-helper hover:bg-surface hover:text-ink',
                )}
              >
                <Icon className="h-5 w-5 shrink-0" strokeWidth={1.8} />
                <span className={cn('truncate', collapsed && 'lg:hidden')}>{t(labelKey)}</span>
              </Link>
            );
          })}
        </nav>

        {footer && (
          <div className={cn('shrink-0 border-t border-hair px-4 py-3', collapsed && 'lg:px-2')}>
            {footer}
          </div>
        )}
      </aside>
    </>
  );
}
